import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import { WHATSAPP_HREF, PHONE_HREF, PHONE_DISPLAY, sendEvent } from "@/lib/tracking";
import { trackConsultationBookingStart } from "@/lib/analytics";

const STEPS = {
  trial: ["We'll call or message you within 24 hours to say hello and answer any questions.", "Book your consultation below — a relaxed 30-minute chat about where you are and where you want to be.", "Your first coached session. We'll show you round, take it easy and set your plan for the 30 days."],
  access: ["We'll be in touch within 24 hours to arrange your look around.", "Come and see the gym floor at a time that suits you — no pressure, no hard sell.", "If it feels right, join KORE Access online or in person on the day."],
  studio: ["We'll come back to you within 24 hours with availability and rates.", "If it's a fit, we'll arrange a visit so you can see the space.", "Agree dates and you're in."],
  contact: ["We'll reply personally within 24 hours.", "If it's easier, call or WhatsApp us in the meantime.", "Come and see us at Rowan House — visits by arrangement."],
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const name = params.get("name") || "";
  const funnel = params.get("funnel") || "contact";
  const dup = params.get("dup") === "1";
  const steps = STEPS[funnel] || STEPS.contact;

  useEffect(() => { if (funnel === "trial") trackConsultationBookingStart({ booking_type: "trial_consultation" }); }, [funnel]);

  return (
    <div data-testid="thank-you-page" className="bg-kore-warm">
      <Seo title="Thank you" description="We've received your enquiry." path="/thank-you" noindex />
      <section className="container-kore pt-16 pb-12 max-w-3xl">
        <span className="eyebrow">Received</span>
        <h1 className="display-1 mt-4 text-kore-forest">Thanks{name ? `, ${name}` : ""}. <span className="italic text-kore-gold">We've got it.</span></h1>
        {dup && (
          <p data-testid="thank-you-duplicate" className="mt-6 rounded-2xl bg-kore-light/30 border border-kore-gold/25 px-5 py-4 text-kore-ink/80 leading-relaxed">
            It looks like we already have your details from the last few weeks — no need to send them again. We'll be in touch on the number or email you gave us.
          </p>
        )}
        <ol data-testid="thank-you-steps" className="mt-12 space-y-8">
          {steps.map((s, i) => (
            <li key={i} className="grid grid-cols-[3rem_1fr] gap-4 items-start">
              <span className="font-display text-3xl text-kore-gold">0{i + 1}</span>
              <p className="text-[17px] text-kore-ink/80 leading-relaxed pt-1">{s}</p>
            </li>
          ))}
        </ol>
        <div className="mt-12 flex flex-wrap gap-4">
          <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" data-testid="thank-you-whatsapp" onClick={() => sendEvent("whatsapp_click", { funnel, label: "thank_you" })} className="btn-primary"><MessageCircle size={15} /> Message us on WhatsApp</a>
          <a href={PHONE_HREF} data-testid="thank-you-phone" onClick={() => sendEvent("phone_click", { funnel, label: "thank_you" })} className="btn-ghost"><Phone size={15} /> {PHONE_DISPLAY}</a>
        </div>
      </section>

      {funnel === "trial" && (
        <section data-testid="thank-you-calendly" className="bg-kore-forest text-kore-warm py-20">
          <div className="container-kore">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="eyebrow text-kore-light">Step 02</span>
              <h2 className="display-2 mt-4">Book your consultation.</h2>
              <p className="mt-4 text-kore-warm/75">Pick a time that suits you. It's free and it's relaxed.</p>
            </div>
            <div className="max-w-4xl mx-auto rounded-3xl bg-white overflow-hidden shadow-lift">
              <CalendlyEmbed prefill={{ name }} utm={{ utmSource: "korewellbeing.com", utmMedium: "web", utmCampaign: "30-day-trial-form" }} height={760} bookingType="trial_consultation" />
            </div>
          </div>
        </section>
      )}

      <section className="container-kore py-16 text-center">
        <Link to="/" data-testid="thank-you-home" className="text-sm text-kore-forest border-b border-kore-gold pb-1 inline-flex items-center gap-2">Back to KORE Wellbeing <ArrowRight size={14} /></Link>
      </section>
    </div>
  );
}
